import { ReactElement, useState, useCallback, useEffect } from 'react';
import styles from './onboarding-company-name.module.scss';
import { UIButton, UIFormInput, UISpacer, UIText, UITextButton } from '@/ui';
import { COMPANY_NAME_MAX_LENGTH, validateCompanyName } from '@/common/util';
import { useCompaniesTranslation } from '../hooks';
import NationalEnergyFoundationLogo from '../assets/images/logos/national-energy-foundation.png';
import EastOfEnglandCoopDeliveryLogo from '../assets/images/logos/east-of-england-co-op.png';

type OnboardingCompanyNameProps = {
    initialCompanyName: string;
    onChange: (companyName: string) => void;
    onContinue: () => void;
    onBack: () => void;
};

export function OnboardingCompanyName(
    props: OnboardingCompanyNameProps
): ReactElement {
    const { initialCompanyName, onChange, onContinue, onBack } = props;

    const { t } = useCompaniesTranslation();

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Company Name
     */
    const [companyName, setCompanyName] = useState(initialCompanyName);
    const [companyNameError, setCompanyNameError] = useState('');
    const checkCompanyName = useCallback(
        (companyName: string): string => {
            const error = validateCompanyName(companyName);
            if (error) {
                return t(error);
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        setCompanyNameError(checkCompanyName(companyName));
        onChange(companyName);
    }, [companyName, checkCompanyName, onChange]);

    const handleContinue = useCallback(() => {
        if (companyNameError) {
            setShowRealTimeErrors(true);
        } else {
            onContinue();
        }
    }, [companyNameError, onContinue]);

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                handleContinue();
            }
        };

        document.addEventListener('keydown', handleKeyPress);

        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [handleContinue]);

    return (
        <div className={styles.container}>
            <div className={styles.firstHalf}>
                <div className={styles.content}>
                    <div>
                        <UIText variant="h1" bold>
                            {t('components.onboarding_company_name.title')}
                        </UIText>

                        <UISpacer multiplier={2} />

                        <UIFormInput
                            name="companyName"
                            label={t('shared.fields.company_name.label')}
                            value={companyName}
                            placeholder={t(
                                'shared.fields.company_name.placeholder'
                            )}
                            type="text"
                            errorText={
                                showRealTimeErrors ? companyNameError : ''
                            }
                            autoFocus
                            required
                            maxLength={COMPANY_NAME_MAX_LENGTH}
                            onChange={setCompanyName}
                        />

                        <UISpacer multiplier={2} />

                        <div className={styles.buttonContainer}>
                            <UIButton
                                variant={'primary'}
                                onClick={handleContinue}
                            >
                                {t('shared.labels.continue')}
                            </UIButton>
                            <UISpacer multiplier={2} />
                            <UITextButton onClick={onBack}>
                                {t('shared.labels.back')}
                            </UITextButton>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.secondHalf}>
                <div className={styles.quotes}>
                    <div className={styles.quoteContainer}>
                        <img
                            src={NationalEnergyFoundationLogo}
                            alt={t(
                                'components.onboarding_company_name.nef_logo_alt'
                            )}
                            className={styles.logoImage}
                        />

                        <UIText variant="large" type="link" textAlign="center">
                            {t('components.onboarding_company_name.nef_quote')}
                        </UIText>
                        <UIText
                            variant="basic"
                            medium
                            type="default2"
                            textAlign="center"
                        >
                            {t(
                                'components.onboarding_company_name.nef_attribution'
                            )}
                        </UIText>
                    </div>

                    <div className={styles.quoteContainer}>
                        <img
                            src={EastOfEnglandCoopDeliveryLogo}
                            alt={t(
                                'components.onboarding_company_name.coop_logo_alt'
                            )}
                            className={styles.logoImage}
                        />

                        <UIText variant="large" type="link" textAlign="center">
                            {t('components.onboarding_company_name.coop_quote')}
                        </UIText>
                        <UIText
                            variant="basic"
                            medium
                            type="default2"
                            textAlign="center"
                        >
                            {t(
                                'components.onboarding_company_name.coop_attribution'
                            )}
                        </UIText>
                    </div>
                </div>
            </div>
        </div>
    );
}
